/**
 * features/admin/ActivityLog.tsx
 * ------------------------------------------------------------------
 * Enhanced audit trail viewer. Shows all logged actions with full
 * detail expansion, filtering by category/actor/date, and CSV export.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  Shield,
  Search,
  Download,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  FileText,
  // Round 8: PDF export
  FileDown,
} from 'lucide-react';
import jsPDF from 'jspdf';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { SCHOOL } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';

interface AuditEvent {
  id: string;
  action: string;
  category: string;
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
  actorRole?: string;
  targetId?: string;
  targetType?: string;
  targetName?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  device?: {
    userAgent?: string;
    platform?: string;
    language?: string;
    screenSize?: string;
    timezone?: string;
    displayMode?: string;
  };
  createdAt: string;
}

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'admin', label: 'Administration' },
  { value: 'academic', label: 'Academic' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'discipline', label: 'Discipline' },
  { value: 'admissions', label: 'Admissions' },
  { value: 'communication', label: 'Communication' },
  { value: 'finance', label: 'Finance' },
  { value: 'hr', label: 'HR' },
];

const CATEGORY_COLORS: Record<string, string> = {
  auth: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
  academic: 'bg-blue-100 text-blue-700',
  attendance: 'bg-emerald-100 text-emerald-700',
  discipline: 'bg-amber-100 text-amber-700',
  admissions: 'bg-indigo-100 text-indigo-700',
  communication: 'bg-sky-100 text-sky-700',
  finance: 'bg-teal-100 text-teal-700',
  hr: 'bg-orange-100 text-orange-700',
};

export default function ActivityLog() {
  const { userData } = useAuth();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [maxResults, setMaxResults] = useState(200);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.auditLog),
          orderBy('createdAt', 'desc'),
          limit(maxResults),
        ),
      );
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as AuditEvent));
    } catch (e) {
      console.error('Failed to load audit log:', e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return events.filter((e) => {
      if (cat !== 'all' && e.category !== cat) return false;
      if (q) {
        const searchable = [
          e.action, e.actorName, e.actorEmail, e.actorRole,
          e.targetName, e.targetType, e.targetId,
          JSON.stringify(e.details ?? {}),
        ].join(' ').toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
  }, [events, cat, searchQuery]);

  function exportCsv() {
    const headers = ['Timestamp', 'Action', 'Category', 'Actor', 'Actor Email', 'Actor Role', 'IP Address', 'Target', 'Target Type', 'Platform', 'Details'];
    const rows = filtered.map((e) => [
      e.createdAt,
      e.action,
      e.category,
      `"${e.actorName ?? e.actorId ?? ''}"`,
      e.actorEmail ?? '',
      e.actorRole ?? '',
      e.ipAddress ?? '',
      `"${e.targetName ?? e.targetId ?? ''}"`,
      e.targetType ?? '',
      e.device?.platform ?? '',
      `"${JSON.stringify(e.details ?? {}).replace(/"/g, '""')}"`,
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ===============================================================
     Round 8 — PDF export
     ===============================================================
     Reflects whatever's currently visible on screen (`filtered`),
     so changing the category / search / max-results changes the
     PDF. Header carries school branding via loadLogoDataUrl + the
     SCHOOL constant — matches the look of transcripts exported
     from reportPdf.ts.

     Layout choices:
       - Landscape A4 (297 x 210mm) to fit the 7 columns
       - Hand-rolled table (no jspdf-autotable in deps)
       - splitTextToSize per cell, row height = max line count
       - Page-break BEFORE writing each row, so headers always
         repeat at the top of every page
       - "details" rendered as full pretty-printed JSON; can make
         rows very tall but every byte of information is preserved
  =============================================================== */
  async function exportPdf() {
    // Geometry
    const M = 10;                  // page margin (mm)
    const PAGE_W = 297;            // landscape A4 width
    const PAGE_H = 210;            // landscape A4 height
    const HEADER_H = 28;           // school header strip
    const FILTER_H = 12;           // filter summary strip
    const TABLE_TOP = HEADER_H + FILTER_H + 6;
    const FOOTER_Y = PAGE_H - 7;
    // Column widths sum to (PAGE_W - 2*M) = 277
    const COLS = [
      { key: 'time',     label: 'Timestamp', w: 28 },
      { key: 'category', label: 'Category',  w: 20 },
      { key: 'actor',    label: 'Actor',     w: 38 },
      { key: 'action',   label: 'Action',    w: 40 },
      { key: 'target',   label: 'Target',    w: 40 },
      { key: 'ip',       label: 'IP',        w: 22 },
      { key: 'details',  label: 'Details',   w: 89 },
    ] as const;

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    const logo = await loadLogoDataUrl();

    function drawHeader() {
      // Round 8b: printer-friendly header — text only, no banner fill.
      // The original navy block looked nice on screen but cost a lot
      // of toner when staff print these out. Logo stays (small, top-left)
      // since it's incidental on a black-and-white print.
      if (logo) {
        try {
          pdf.addImage(logo, 'PNG', M, 6, 14, 14);
        } catch {
          /* logo failed — ignore */
        }
      }
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(SCHOOL.name, M + 18, 12);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.text('Activity Log Export', M + 18, 18);
      // Thin separator line under the header
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.2);
      pdf.line(M, HEADER_H - 4, PAGE_W - M, HEADER_H - 4);
      // Reset for body
      pdf.setTextColor(0, 0, 0);
    }

    function drawFilterStrip() {
      // Round 8b: no fill — just plain text + a thin underline.
      const summaryLeft = [
        `Category: ${cat === 'all' ? 'All' : cat}`,
        searchQuery ? `Search: "${searchQuery}"` : null,
        `${filtered.length} event${filtered.length === 1 ? '' : 's'}`,
      ]
        .filter(Boolean)
        .join('  ·  ');
      const summaryRight = [
        `Generated ${new Date().toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}`,
        userData?.displayName ? `by ${userData.displayName}` : null,
      ]
        .filter(Boolean)
        .join(' ');
      pdf.setFontSize(8);
      pdf.setTextColor(60, 60, 60);
      pdf.text(summaryLeft, M, HEADER_H + 5);
      pdf.text(summaryRight, PAGE_W - M, HEADER_H + 5, { align: 'right' });
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.2);
      pdf.line(M, HEADER_H + FILTER_H - 2, PAGE_W - M, HEADER_H + FILTER_H - 2);
      pdf.setTextColor(0, 0, 0);
    }

    function drawColumnHeaders() {
      // Round 8b: bold + underline, no fill.
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      let x = M;
      COLS.forEach((c) => {
        pdf.text(c.label, x + 1.5, TABLE_TOP + 4);
        x += c.w;
      });
      pdf.setDrawColor(80, 80, 80);
      pdf.setLineWidth(0.3);
      pdf.line(M, TABLE_TOP + 6, PAGE_W - M, TABLE_TOP + 6);
      pdf.setFont('helvetica', 'normal');
    }

    function drawFooter(pageNum: number, pageTotal: number) {
      pdf.setFontSize(7);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`${SCHOOL.shortName} · Activity Log`, M, FOOTER_Y);
      pdf.text(`Page ${pageNum} of ${pageTotal}`, PAGE_W - M, FOOTER_Y, {
        align: 'right',
      });
      pdf.setTextColor(0, 0, 0);
    }

    function fmtTime(iso: string): string {
      try {
        const d = new Date(iso);
        return (
          d.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          }) +
          ' ' +
          d.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
          })
        );
      } catch {
        return iso;
      }
    }

    // Pre-compute per-row data so we can two-pass for page totals
    const ROW_VPAD = 1.5;   // mm above + below text
    const LINE_H = 3.4;     // mm per text line at fontSize 8
    const rowsData = filtered.map((e) => {
      const cells = {
        time: fmtTime(e.createdAt),
        category: e.category,
        actor:
          [e.actorName ?? e.actorId ?? '—', e.actorRole]
            .filter(Boolean)
            .join('\n'),
        action: formatAction(e.action),
        target:
          [e.targetName ?? e.targetId ?? '—', e.targetType]
            .filter(Boolean)
            .join('\n'),
        ip: e.ipAddress ?? '',
        details:
          e.details && Object.keys(e.details).length > 0
            ? JSON.stringify(e.details, null, 2)
            : '',
      };
      // Wrap each cell to its column width and compute row height
      pdf.setFontSize(8);
      const wrapped: Record<string, string[]> = {};
      let maxLines = 1;
      // Hard cap so a 200-line details JSON doesn't render an unprintable
      // row. ~40 lines at LINE_H=3.4 + padding fits comfortably in one page.
      const MAX_LINES = 40;
      COLS.forEach((c) => {
        const raw = (cells as Record<string, string>)[c.key] ?? '';
        // splitTextToSize uses current font + size; subtract padding
        let lines = raw
          ? (pdf.splitTextToSize(raw, c.w - 3) as string[])
          : [''];
        if (lines.length > MAX_LINES) {
          lines = [
            ...lines.slice(0, MAX_LINES - 1),
            `… (${lines.length - MAX_LINES + 1} more lines truncated — see CSV for full details)`,
          ];
        }
        wrapped[c.key] = lines;
        if (lines.length > maxLines) maxLines = lines.length;
      });
      const height = maxLines * LINE_H + ROW_VPAD * 2;
      return { wrapped, height };
    });

    // First pass: page-break planning — figure out total page count
    const TABLE_BOTTOM = FOOTER_Y - 4;
    const TABLE_HEADER_BOTTOM = TABLE_TOP + 6;
    let cursorY = TABLE_HEADER_BOTTOM;
    let pageTotal = 1;
    rowsData.forEach((r) => {
      if (cursorY + r.height > TABLE_BOTTOM) {
        pageTotal += 1;
        cursorY = TABLE_HEADER_BOTTOM;
      }
      cursorY += r.height;
    });

    // Second pass: actually render
    let pageNum = 1;
    drawHeader();
    drawFilterStrip();
    drawColumnHeaders();
    drawFooter(pageNum, pageTotal);
    cursorY = TABLE_HEADER_BOTTOM;

    if (rowsData.length === 0) {
      pdf.setFontSize(11);
      pdf.setTextColor(120, 120, 120);
      pdf.text(
        'No events matched the current filter.',
        PAGE_W / 2,
        TABLE_HEADER_BOTTOM + 20,
        { align: 'center' },
      );
      pdf.setTextColor(0, 0, 0);
    } else {
      pdf.setFontSize(8);
      rowsData.forEach((r) => {
        // Page break?
        if (cursorY + r.height > TABLE_BOTTOM) {
          pdf.addPage();
          pageNum += 1;
          drawHeader();
          drawFilterStrip();
          drawColumnHeaders();
          drawFooter(pageNum, pageTotal);
          cursorY = TABLE_HEADER_BOTTOM;
        }
        // Round 8b: no alternating fill — the bottom-border lines
        // between rows give enough visual rhythm and saves toner.
        // Render each cell
        let x = M;
        COLS.forEach((c) => {
          const lines = r.wrapped[c.key];
          pdf.text(lines, x + 1.5, cursorY + ROW_VPAD + 2.5);
          x += c.w;
        });
        // Bottom border
        pdf.setDrawColor(229, 231, 235);
        pdf.line(M, cursorY + r.height, PAGE_W - M, cursorY + r.height);
        cursorY += r.height;
      });
    }

    // Save
    const filterTag =
      cat === 'all' ? 'all' : cat.replace(/[^a-z0-9]/gi, '_');
    pdf.save(
      `activity_log_${filterTag}_${new Date().toISOString().slice(0, 10)}.pdf`,
    );
  }

  function formatAction(action: string): string {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function formatTime(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  }

  function renderDetails(details: Record<string, unknown>): JSX.Element {
    return (
      <div className="mt-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-xs font-mono space-y-1 max-h-[300px] overflow-y-auto">
        {Object.entries(details).map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <span className="text-gray-500 shrink-0 w-32 truncate">{key}:</span>
            <span className="text-gray-800 dark:text-gray-200 break-all">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (loading) return <Spinner label="Loading audit log…" />;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Shield}
        title="Activity Log"
        subtitle={`${events.length} events · Full audit trail of system actions`}
      />

      {/* Filters */}
      <SoftCard pad="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search actions, users, targets…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={glassFieldClass('pl-9')}
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={glassFieldClass('sm:w-48')}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <AccentButton variant="soft" icon={Download} onClick={exportCsv}>Export CSV</AccentButton>
          <AccentButton variant="soft" icon={FileDown} onClick={exportPdf}>Export PDF</AccentButton>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Showing {filtered.length} of {events.length} events
        </p>
      </SoftCard>

      {/* Events list */}
      <SoftCard pad="p-0">
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No matching events found.</p>
          ) : (
            filtered.map((e) => {
              const expanded = expandedId === e.id;
              return (
                <div key={e.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/10 transition-colors">
                  <button
                    onClick={() => setExpandedId(expanded ? null : e.id)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3"
                  >
                    <div className="shrink-0 mt-0.5">
                      {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900 dark:text-white">
                          {formatAction(e.action)}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[e.category] ?? 'bg-gray-100 text-gray-600'}`}>
                          {e.category}
                        </span>
                        {e.targetName && (
                          <span className="text-xs text-gray-500">→ {e.targetName}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> {formatTime(e.createdAt)}
                        </span>
                        {(e.actorName || e.actorEmail) && (
                          <span className="flex items-center gap-1">
                            <User size={11} /> {e.actorName ?? e.actorEmail}
                          </span>
                        )}
                        {e.actorRole && (
                          <span className="capitalize">{e.actorRole}</span>
                        )}
                        {e.details && Object.keys(e.details).length > 0 && (
                          <span className="flex items-center gap-1 text-primary">
                            <FileText size={11} /> Details
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  {/* Expanded details */}
                  {expanded && (
                    <div className="px-4 pb-3 ml-7">
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs mb-2">
                        {e.actorId && <div><span className="text-gray-500">Actor ID:</span> <span className="text-gray-700 dark:text-gray-300 font-mono">{e.actorId}</span></div>}
                        {e.actorEmail && <div><span className="text-gray-500">Actor Email:</span> <span className="text-gray-700 dark:text-gray-300">{e.actorEmail}</span></div>}
                        {e.actorRole && <div><span className="text-gray-500">Actor Role:</span> <span className="text-gray-700 dark:text-gray-300 capitalize">{e.actorRole}</span></div>}
                        {e.targetId && <div><span className="text-gray-500">Target ID:</span> <span className="text-gray-700 dark:text-gray-300 font-mono">{e.targetId}</span></div>}
                        {e.targetType && <div><span className="text-gray-500">Target Type:</span> <span className="text-gray-700 dark:text-gray-300 capitalize">{e.targetType}</span></div>}
                        {e.targetName && <div><span className="text-gray-500">Target:</span> <span className="text-gray-700 dark:text-gray-300">{e.targetName}</span></div>}
                        {e.ipAddress && <div><span className="text-gray-500">IP Address:</span> <span className="text-gray-700 dark:text-gray-300 font-mono">{e.ipAddress}</span></div>}
                        {e.device?.platform && <div><span className="text-gray-500">Platform:</span> <span className="text-gray-700 dark:text-gray-300">{e.device.platform}</span></div>}
                        {e.device?.timezone && <div><span className="text-gray-500">Timezone:</span> <span className="text-gray-700 dark:text-gray-300">{e.device.timezone}</span></div>}
                        {e.device?.screenSize && <div><span className="text-gray-500">Screen:</span> <span className="text-gray-700 dark:text-gray-300">{e.device.screenSize}</span></div>}
                        {e.device?.displayMode && <div><span className="text-gray-500">App Mode:</span> <span className="text-gray-700 dark:text-gray-300">{e.device.displayMode}</span></div>}
                        {e.device?.userAgent && <div className="sm:col-span-2"><span className="text-gray-500">User Agent:</span> <span className="text-gray-700 dark:text-gray-300 text-[10px] break-all">{e.device.userAgent}</span></div>}
                      </div>
                      {e.details && Object.keys(e.details).length > 0 && renderDetails(e.details)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SoftCard>

      {events.length >= maxResults && (
        <div className="text-center">
          <AccentButton variant="soft" onClick={() => setMaxResults((m) => m + 200)}>
            Load more events
          </AccentButton>
        </div>
      )}
    </div>
  );
}
